import { DeviceTelemetryType } from '@signageos/common-types/dist/Device/Telemetry/DeviceTelemetryType';
import { createDeviceTelemetrySaga } from '../Device/Telemetry/deviceTelemetrySagas';
import Responsibility from '../../Feature/Responsibility';
import ManagementCapability from '../../NativeDevice/Management/ManagementCapability';
import IGetTelemetryProperties from '../Device/Telemetry/IGetTelemetryProperties';
import Property from '../../Property/Property';
import { AssignedPlugins } from '../../Plugins/pluginsSaga';
import { BrowserRuntime } from '../../CustomScripts/Runtimes/browserRuntime';
import { call, cancel, fork } from 'redux-saga/effects';
import { buffers, channel } from 'redux-saga';

// Constants for plugin telemetry configuration
const TELEMETRY_PLUGIN_WAIT_TIMEOUT = 2000;
const TELEMETRY_PLUGIN_RETRY_COUNT = 20;
const TELEMETRY_PLUGIN_RETRY_DELAY = 200;

/**
 * Utility function to get assigned plugins from property storage
 */
function* getAssignedPlugins(propertyStorage: any): any {
	const assignedPlugins: AssignedPlugins = yield propertyStorage.getValueOrDefault(Property.DEVICE_PLUGINS, {});
	return assignedPlugins;
}

/**
 * Utility function to get plugin iframe window
 */
function getPluginIframeWindow(iframeId: string): { iframe: HTMLIFrameElement | null; iframeWindow: any } {
	const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
	const iframeWindow = iframe?.contentWindow as any;
	return { iframe, iframeWindow };
}

/**
 * Utility function to wait for sosPlugin to be available in iframe
 */
function* waitForSosPlugin(iframeWindow: any, pluginUid: string): any {
	const waitForModule = (retries = TELEMETRY_PLUGIN_RETRY_COUNT): Promise<any> => {
		return new Promise((resolve, reject) => {
			const checkModule = () => {
				if (iframeWindow && iframeWindow.sosPlugin && iframeWindow.sosPlugin.get) {
					resolve(iframeWindow.sosPlugin);
				} else if (retries > 0) {
					setTimeout(checkModule, TELEMETRY_PLUGIN_RETRY_DELAY);
					retries--;
				} else {
					reject(new Error(`sosPlugin not available for plugin ${pluginUid}`));
				}
			};
			checkModule();
		});
	};

	return yield call(waitForModule);
}

/**
 * Utility function to create and run plugin iframe for telemetry
 */
function* createAndRunPluginForTelemetry(
	browserRuntime: BrowserRuntime,
	managementDriver: any,
	plugin: AssignedPlugins[string],
	iframeId: string,
): any {
	const messageChannel = channel<MessageEvent>(buffers.dropping(100));
	const messagesHandler = (event: MessageEvent) => {
		messageChannel.put(event);
	};

	yield call(
		browserRuntime.runPluginInBrowser,
		managementDriver,
		plugin,
		plugin.configuration || {},
		plugin.pluginFile,
		plugin.appletJsFile,
		messagesHandler,
	);

	const customMessages = yield fork(browserRuntime.bindCustomScriptsMessages, messageChannel, window, iframeId);
	yield new Promise((resolve) => setTimeout(resolve, TELEMETRY_PLUGIN_WAIT_TIMEOUT));

	return { messageChannel, customMessages };
}

/**
 * Utility function to cleanup plugin telemetry resources
 */
function* cleanupPluginTelemetryResources(iframe: HTMLIFrameElement | null, customMessages: any): any {
	if (customMessages) {
		yield cancel(customMessages);
	}
	if (iframe?.parentNode) {
		iframe.parentNode.removeChild(iframe);
	}
}

/**
 * Utility function to get telemetry data from plugin
 */
function* getPluginTelemetryData(plugin: AssignedPlugins[string], managementDriver: any): any {
	const iframeId = plugin.md5Checksum;
	let { iframe } = getPluginIframeWindow(iframeId);
	let customMessages: any = null;
	let createdIframe = false;

	try {
		// Create iframe if it doesn't exist
		if (!iframe) {
			const browserRuntime = new BrowserRuntime();
			const result = yield* createAndRunPluginForTelemetry(browserRuntime, managementDriver, plugin, iframeId);
			customMessages = result.customMessages;
			iframe = getPluginIframeWindow(iframeId).iframe;
			createdIframe = true;
		}

		if (!iframe?.contentWindow) {
			return {
				pluginUid: plugin.pluginUid,
				version: plugin.pluginVersion,
				data: { error: 'Iframe not found' },
			};
		}

		const { iframeWindow } = getPluginIframeWindow(iframeId);
		const sosPlugin = yield* waitForSosPlugin(iframeWindow, plugin.pluginUid);
		const telemetryData = yield call(sosPlugin.get);
		if (createdIframe) {
			yield* cleanupPluginTelemetryResources(iframe, customMessages);
		}
		return {
			pluginUid: plugin.pluginUid,
			version: plugin.pluginVersion,
			data: telemetryData || {},
		};
	} catch (error) {
		return {
			pluginUid: plugin.pluginUid,
			version: plugin.pluginVersion,
			data: { error: 'Failed to get telemetry', details: error.message },
		};
	} finally {
		if (createdIframe) {
			yield* cleanupPluginTelemetryResources(iframe, customMessages);
		}
	}
}

export const telemetryPluginsSaga = createDeviceTelemetrySaga(
	DeviceTelemetryType.PLUGIN,
	Responsibility.PLUGINS,
	[ManagementCapability.PLUGINS],
	getPluginSettings,
);

export function* getPluginSettings({
	propertyStorage,
	managementDriver,
}: IGetTelemetryProperties & { propertyStorage: any; managementDriver: any }): any {
	try {
		const assignedPlugins = yield* getAssignedPlugins(propertyStorage);
		const pluginEntries = Object.entries(assignedPlugins);

		if (pluginEntries.length === 0) {
			return [];
		}

		const pluginDataList: { pluginUid: string; version: string; data: object }[] = [];

		for (const [_assignmentUid, plugin] of pluginEntries) {
			const typedPlugin = plugin as AssignedPlugins[string];

			try {
				const telemetryResult = yield* getPluginTelemetryData(typedPlugin, managementDriver);
				pluginDataList.push(telemetryResult);
			} catch (error) {
				pluginDataList.push({
					pluginUid: typedPlugin.pluginUid,
					version: typedPlugin.pluginVersion,
					data: { error: 'Failed to process plugin', details: error.message },
				});
			}
		}

		return pluginDataList.length > 0 ? pluginDataList : [];
	} catch (error) {
		return [];
	}
}
